import React, { useState } from 'react';
import { Plus, X, Save } from 'lucide-react';
import stripeApiService from '../services/stripeApiService';

/**
 * Composant pour créer de nouveaux prix pour les produits Stripe
 */
const StripePriceCreator = ({ product, onPriceCreated, onCancel }) => {
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [priceData, setPriceData] = useState({
    unitAmount: '',
    currency: 'eur',
    nickname: '',
    recurring: {
      interval: 'month',
      interval_count: 1
    }
  });

  // Réinitialiser le formulaire
  const resetForm = () => {
    setPriceData({
      unitAmount: '',
      currency: 'eur',
      nickname: '',
      recurring: {
        interval: 'month',
        interval_count: 1
      }
    });
    setError(null);
  };

  // Créer un nouveau prix
  const createPrice = async () => {
    if (!priceData.unitAmount || priceData.unitAmount <= 0) {
      setError('Veuillez saisir un montant valide');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const priceDataToSend = {
        productId: product.id,
        unitAmount: parseFloat(priceData.unitAmount),
        currency: priceData.currency,
        nickname: priceData.nickname || undefined,
        recurring: {
          interval: priceData.recurring.interval,
          interval_count: parseInt(priceData.recurring.interval_count)
        }
      };

      await stripeApiService.createPrice(priceDataToSend);
      
      // Réinitialiser et fermer
      resetForm();
      setShowForm(false);
      
      // Notifier le parent
      if (onPriceCreated) {
        onPriceCreated();
      }
    } catch (error) {
      console.error('Erreur lors de la création du prix:', error);
      setError(`Erreur lors de la création: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Annuler la création
  const handleCancel = () => {
    resetForm();
    setShowForm(false);
    if (onCancel) {
      onCancel();
    }
  };

  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        className="flex items-center px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
        title="Ajouter un nouveau prix"
      >
        <Plus className="w-4 h-4 mr-1" />
        Nouveau prix
      </button>
    );
  }

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h5 className="font-semibold text-blue-800 dark:text-blue-300">
          Nouveau prix pour "{product.name}"
        </h5>
        <button
          onClick={handleCancel}
          className="p-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3 mb-4">
          <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Montant */}
        <div>
          <label className="block text-sm font-medium text-blue-700 dark:text-blue-400 mb-1">
            Montant *
          </label>
          <div className="flex">
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={priceData.unitAmount}
              onChange={(e) => setPriceData({ ...priceData, unitAmount: e.target.value })}
              className="flex-1 px-3 py-2 border border-blue-300 dark:border-blue-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="15.00"
            />
            <select
              value={priceData.currency}
              onChange={(e) => setPriceData({ ...priceData, currency: e.target.value })}
              className="px-3 py-2 border border-blue-300 dark:border-blue-600 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="eur">EUR</option>
              <option value="usd">USD</option>
              <option value="gbp">GBP</option>
            </select>
          </div>
        </div>

        {/* Nom d'affichage */}
        <div>
          <label className="block text-sm font-medium text-blue-700 dark:text-blue-400 mb-1">
            Nom d'affichage (optionnel)
          </label>
          <input
            type="text"
            value={priceData.nickname}
            onChange={(e) => setPriceData({ ...priceData, nickname: e.target.value })}
            className="w-full px-3 py-2 border border-blue-300 dark:border-blue-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="ex: Mensuel, Annuel, Premium"
          />
        </div>

        {/* Période */}
        <div>
          <label className="block text-sm font-medium text-blue-700 dark:text-blue-400 mb-1">
            Période
          </label>
          <div className="flex space-x-2">
            <select
              value={priceData.recurring.interval}
              onChange={(e) => setPriceData({ 
                ...priceData, 
                recurring: { ...priceData.recurring, interval: e.target.value }
              })}
              className="flex-1 px-3 py-2 border border-blue-300 dark:border-blue-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="day">Jour</option>
              <option value="week">Semaine</option>
              <option value="month">Mois</option>
              <option value="year">An</option>
            </select>
            <input
              type="number"
              min="1"
              max="12"
              value={priceData.recurring.interval_count}
              onChange={(e) => setPriceData({ 
                ...priceData, 
                recurring: { ...priceData.recurring, interval_count: e.target.value }
              })}
              className="w-20 px-3 py-2 border border-blue-300 dark:border-blue-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="1"
            />
          </div>
        </div>

        {/* Aperçu */}
        <div>
          <label className="block text-sm font-medium text-blue-700 dark:text-blue-400 mb-1">
            Aperçu
          </label>
          <div className="px-3 py-2 bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded-md">
            <p className="text-blue-800 dark:text-blue-200 text-sm">
              {priceData.unitAmount ? (
                <>
                  <strong>{parseFloat(priceData.unitAmount).toFixed(2)} {priceData.currency.toUpperCase()}</strong>
                  {' '}
                  {(() => {
                    // Traduire les intervalles en français
                    const intervalTranslations = {
                      'day': 'jour',
                      'week': 'semaine',
                      'month': 'mois',
                      'year': 'an'
                    };
                    
                    const intervalFr = intervalTranslations[priceData.recurring.interval] || priceData.recurring.interval;
                    
                    if (priceData.recurring.interval_count > 1) {
                      return `tous les ${priceData.recurring.interval_count} ${intervalFr}s`;
                    } else {
                      return `par ${intervalFr}`;
                    }
                  })()}
                </>
              ) : (
                'Saisissez un montant pour voir l\'aperçu'
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Informations importantes */}
      <div className="mt-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
        <p className="text-yellow-700 dark:text-yellow-400 text-xs">
          <strong>Important :</strong> Dans Stripe, les prix sont immuables. Vous ne pouvez pas modifier le montant d'un prix existant. 
          Créez un nouveau prix avec le montant souhaité, puis désactivez l'ancien prix si nécessaire.
        </p>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-2 mt-4">
        <button
          onClick={handleCancel}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          Annuler
        </button>
        <button
          onClick={createPrice}
          disabled={saving || !priceData.unitAmount}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Création...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-1" />
              Créer le prix
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default StripePriceCreator;
